import { MONTHS_LONG, type IsoDate } from '@koverta/shared';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Avatar, Icon, Sheet, useEscape } from '../components/ui';
import { useClickOutside } from '../lib/hooks';
import {
  DEFAULT_SORT,
  DEFAULT_VIEW,
  RANGE_PRESETS,
  isDefaultView,
  rangeMonths,
  suggest,
  tokenLabel,
  type Range,
  type RegisterView,
  type Sort,
  type SuggestSource,
  type Suggestion,
  type Token,
} from './register-filter';

// ───────── Persisted per-register state ─────────

const storeKey = (id: string) => `koverta.register.${id}`;

function readStored(id: string): { view: RegisterView; sort: Sort } {
  try {
    const raw = JSON.parse(localStorage.getItem(storeKey(id)) ?? '{}');
    return { view: { ...DEFAULT_VIEW, ...raw.view }, sort: raw.sort ?? DEFAULT_SORT };
  } catch {
    return { view: DEFAULT_VIEW, sort: DEFAULT_SORT };
  }
}

/**
 * Search tokens, typed text, view options and sort for one register (`id` = account id or "all").
 * View and sort persist per register; `initial` (from a Plan drill-down link) applies to this visit only.
 */
export function useRegisterState(id: string, initial: { tokens?: Token[]; range?: Range } = {}) {
  const [stored] = useState(() => readStored(id));
  const [tokens, setTokens] = useState<Token[]>(initial.tokens ?? []);
  const [text, setText] = useState('');
  const [view, setViewState] = useState<RegisterView>(() =>
    initial.range ? { ...stored.view, range: initial.range } : stored.view,
  );
  const [sort, setSortState] = useState<Sort>(stored.sort);
  const save = (v: RegisterView, s: Sort) => {
    if (isDefaultView(v) && s.key === DEFAULT_SORT.key && s.dir === DEFAULT_SORT.dir)
      localStorage.removeItem(storeKey(id));
    else localStorage.setItem(storeKey(id), JSON.stringify({ view: v, sort: s }));
  };
  return {
    tokens,
    setTokens,
    text,
    setText,
    view,
    setView: (v: RegisterView) => {
      setViewState(v);
      save(v, sort);
    },
    sort,
    setSort: (s: Sort) => {
      setSortState(s);
      save(view, s);
    },
  };
}

// ───────── Search box ─────────

function SuggestionIcon({ s }: { s: Suggestion }) {
  if (s.icon === 'flag') return <span className={`dot flag-${s.token.id}`} />;
  if (s.icon === 'avatar') return <Avatar id={s.token.id} />;
  if (s.icon) return <Icon name={s.icon} size={14} />;
  return null;
}

const isTyping = (el: Element | null) =>
  !!el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || (el as HTMLElement).isContentEditable);

/**
 * YNAB-style search: one text input. Typing suggests accounts, categories, payees and states; picking
 * one turns it into a removable chip. Text that isn't picked yet still filters, across every field.
 */
export function RegisterSearch({
  tokens,
  onTokens,
  text,
  onText,
  source,
  autoFocus,
  shortcut,
}: {
  tokens: Token[];
  onTokens: (t: Token[]) => void;
  text: string;
  onText: (t: string) => void;
  source: SuggestSource;
  autoFocus?: boolean;
  /** Focus with "/" from anywhere on the page. */
  shortcut?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const items = useMemo(() => suggest(text, source, tokens), [text, source, tokens]);
  const anyIdx = Math.max(
    0,
    items.findIndex((s) => s.find && s.token.field === 'any'),
  );
  const [active, setActive] = useState(anyIdx);
  useEffect(() => setActive(anyIdx), [anyIdx, text]);
  const open = focused && !dismissed && items.length > 0;
  const ref = useClickOutside<HTMLDivElement>(() => setFocused(false), focused);
  const listId = useId();

  useEffect(() => {
    if (!shortcut) return;
    const h = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || isTyping(document.activeElement)) return;
      if (document.querySelector('[role=dialog]')) return;
      e.preventDefault();
      input.current?.focus();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [shortcut]);

  /** Resolve labels for tokens created from a link (only the id is known then). */
  const display = (k: Token): Token => {
    if (k.value || !k.id) return k;
    const pool = k.field === 'account' ? source.accounts : k.field === 'category' ? source.categories : [];
    return { ...k, value: pool?.find((x) => x.id === k.id)?.name ?? '…' };
  };

  const pick = (s: Suggestion) => {
    onTokens([...tokens, s.token]);
    onText('');
    input.current?.focus();
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!items.length) return;
      e.preventDefault();
      setDismissed(false);
      const d = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => (open ? (i + d + items.length) % items.length : i));
    } else if (e.key === 'Enter') {
      if (open && items[active]) {
        e.preventDefault();
        pick(items[active]!);
      }
    } else if (e.key === 'Escape') {
      if (open) setDismissed(true);
      else if (text) onText('');
      else input.current?.blur();
      e.stopPropagation();
    } else if (e.key === 'Backspace' && !text && tokens.length) {
      onTokens(tokens.slice(0, -1));
    }
  };

  return (
    <div className="ss" ref={ref}>
      <div
        className={`ss-box ${focused ? 'focus' : ''} ${tokens.length ? 'has' : ''}`}
        onClick={() => input.current?.focus()}
      >
        <Icon name="search" size={15} />
        {tokens.map((k, i) => (
          <span className="ss-chip" key={`${k.field}:${k.id ?? k.value}:${i}`}>
            {tokenLabel(display(k))}
            <button
              type="button"
              aria-label={`Remove ${tokenLabel(display(k))}`}
              onClick={(e) => {
                e.stopPropagation();
                onTokens(tokens.filter((_, j) => j !== i));
              }}
            >
              <Icon name="x" size={11} stroke={2.4} />
            </button>
          </span>
        ))}
        <input
          ref={input}
          role="combobox"
          aria-label="Search transactions"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open ? `${listId}-${active}` : undefined}
          placeholder={tokens.length ? '' : 'Search accounts, payees, categories…'}
          autoFocus={autoFocus}
          value={text}
          onChange={(e) => {
            onText(e.target.value);
            setDismissed(false);
          }}
          onFocus={() => setFocused(true)}
          onKeyDown={onKey}
        />
        {tokens.length || text ? (
          <button
            type="button"
            className="ss-clear"
            aria-label="Clear search"
            onClick={(e) => {
              e.stopPropagation();
              onTokens([]);
              onText('');
              input.current?.focus();
            }}
          >
            <Icon name="x" size={12} stroke={2.4} />
          </button>
        ) : null}
      </div>
      {open ? (
        <div className="ss-list" role="listbox" id={listId} aria-label="Search suggestions">
          {items.map((s, i) => (
            <div
              key={`${s.group}:${s.token.field}:${s.token.id ?? ''}:${s.label}`}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              aria-label={
                s.find ? `${s.group ? `${s.group}: ` : ''}Find “${s.token.value}” in ${s.label}` : undefined
              }
              className={`ss-opt ${i === active ? 'on' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(s)}
            >
              <span className="ss-g">{s.group ? `${s.group}:` : ''}</span>
              <span className="ss-l">
                {s.find ? (
                  <span>
                    Find “<b>{s.token.value}</b>” in {s.label}
                  </span>
                ) : (
                  <>
                    <SuggestionIcon s={s} />
                    <span className={s.icon === 'flag' ? 'cap' : ''}>{s.label}</span>
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ───────── View options ─────────

function MonthYear({
  label,
  value,
  years,
  onChange,
}: {
  label: string;
  value: string;
  years: number[];
  onChange: (m: string) => void;
}) {
  const [y, m] = value.split('-');
  return (
    <label className="vo-my">
      <span>{label}</span>
      <select
        className="select"
        aria-label={`${label} month`}
        value={m}
        onChange={(e) => onChange(`${y}-${e.target.value}`)}
      >
        {MONTHS_LONG.map((name, i) => (
          <option key={name} value={String(i + 1).padStart(2, '0')}>
            {name}
          </option>
        ))}
      </select>
      <select
        className="select"
        aria-label={`${label} year`}
        value={y}
        onChange={(e) => onChange(`${e.target.value}-${m}`)}
      >
        {years.map((yr) => (
          <option key={yr} value={yr}>
            {yr}
          </option>
        ))}
      </select>
    </label>
  );
}

function ViewForm({
  view,
  today,
  span,
  mobile,
  onApply,
  onCancel,
}: {
  view: RegisterView;
  today: IsoDate;
  span: { from: string; to: string };
  mobile?: boolean;
  onApply: (v: RegisterView) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(view);
  const r = rangeMonths(draft.range, today);
  const from = r.from ?? span.from;
  const to = r.to ?? span.to;
  const y0 = Math.min(Number(span.from.slice(0, 4)), Number(from.slice(0, 4)));
  const y1 = Math.max(Number(span.to.slice(0, 4)), Number(to.slice(0, 4)));
  const years = Array.from({ length: y1 - y0 + 1 }, (_, i) => y0 + i);
  const setRange = (f: string, t: string) =>
    setDraft({ ...draft, range: f <= t ? { from: f, to: t } : { from: t, to: f } });
  const check = (key: 'showReconciled' | 'showFlags' | 'showMemo' | 'showUpcoming', label: string) => (
    <label className="vo-cb">
      <input
        type="checkbox"
        className="cb"
        checked={draft[key]}
        onChange={(e) => setDraft({ ...draft, [key]: e.target.checked })}
      />
      {label}
    </label>
  );
  return (
    <>
      <div className="vo-presets" role="radiogroup" aria-label="Date range">
        {RANGE_PRESETS.map(([p, label]) => (
          <button
            key={p}
            type="button"
            role="radio"
            aria-checked={'preset' in draft.range && draft.range.preset === p}
            className={'preset' in draft.range && draft.range.preset === p ? 'on' : ''}
            onClick={() => setDraft({ ...draft, range: { preset: p } })}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="vo-range">
        <MonthYear label="From" value={from} years={years} onChange={(m) => setRange(m, to)} />
        <MonthYear label="To" value={to} years={years} onChange={(m) => setRange(from, m)} />
      </div>
      <div className="vo-cbs">
        {check('showReconciled', 'Show reconciled transactions')}
        {mobile ? null : check('showFlags', 'Show flags')}
        {mobile ? null : check('showMemo', 'Show memo column')}
        {check('showUpcoming', 'Show upcoming scheduled transactions')}
      </div>
      <div className="vo-f">
        <button type="button" className="link" onClick={() => setDraft(DEFAULT_VIEW)}>
          Reset to defaults
        </button>
        <span className="sp" />
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn primary" onClick={() => onApply(draft)}>
          Apply
        </button>
      </div>
    </>
  );
}

export function rangeLabel(v: RegisterView, today: IsoDate): string | null {
  if ('preset' in v.range) {
    const preset = v.range.preset;
    return preset === 'all' ? null : RANGE_PRESETS.find(([p]) => p === preset)![1];
  }
  const { from, to } = rangeMonths(v.range, today);
  const m = (x: string) => `${MONTHS_LONG[Number(x.slice(5)) - 1]!.slice(0, 3)} ${x.slice(0, 4)}`;
  return from === to ? m(from!) : `${m(from!)} – ${m(to!)}`;
}

/** "View ▾" button with the YNAB view-options popover (a bottom sheet on mobile). */
export function ViewOptions({
  view,
  onChange,
  today,
  span,
  mobile,
}: {
  view: RegisterView;
  onChange: (v: RegisterView) => void;
  today: IsoDate;
  /** First and last month that have transactions, for the year pickers and "All Dates". */
  span: { from: string; to: string };
  mobile?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false), open && !mobile);
  const label = rangeLabel(view, today);
  const changed = !isDefaultView(view);
  const form = (
    <ViewForm
      view={view}
      today={today}
      span={span}
      mobile={mobile}
      onCancel={() => setOpen(false)}
      onApply={(v) => {
        onChange(v);
        setOpen(false);
      }}
    />
  );
  return (
    // Mobile renders the sheet outside any positioned wrapper, so it anchors to the phone frame.
    mobile ? (
      <>
        <button
          className={`ib ${changed ? 'on' : ''}`}
          onClick={() => setOpen(true)}
          aria-label="View options"
          aria-haspopup="dialog"
        >
          <Icon name="filter" />
        </button>
        {open ? (
          <Sheet onClose={() => setOpen(false)} label="View options">
            <div className="sh-h">
              <b>View options</b>
            </div>
            <div className="vo-body">{form}</div>
          </Sheet>
        ) : null}
      </>
    ) : (
      <div className="vo" ref={ref}>
        <button
          type="button"
          className={`vo-btn ${changed ? 'on' : ''}`}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          View{label ? <span className="vo-tag">{label}</span> : changed ? <span className="vo-dot" /> : null}
          <Icon name="chevD" size={14} />
        </button>
        {open ? <ViewPopover onClose={() => setOpen(false)}>{form}</ViewPopover> : null}
      </div>
    )
  );
}

function ViewPopover({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  useEscape(onClose);
  return (
    <div className="vo-pop" role="dialog" aria-label="View options">
      <h3>View options</h3>
      {children}
    </div>
  );
}
